import { ALERT_COLOR, LANE_COLORS } from "./pipelineColors";

interface Props {
  cx: number;
  cy: number;
  name: string;
  nativeLane: string;
  /** The native lane's configured colour, from `view.lanes`. */
  laneColor?: string;
  ringStyle?: "baseline" | "over" | "drift" | "under" | "none";
  badgeText?: string;
  opacity?: number;
}

/**
 * Avatar circle with optional WIP ring or drift ring, and an optional duration badge.
 * Ring encoding:
 *   baseline: thin translucent ring in native-lane color
 *   over: thick red ring (WIP > baseline * multiplier)
 *   drift: dashed red ring (person is in a non-native lane)
 *   under: thin ring 0.7 stroke
 *   none: no ring
 */
export function AvatarChip({
  cx, cy, name, nativeLane, laneColor: configured,
  ringStyle = "baseline",
  badgeText,
  opacity = 1,
}: Props) {
  // The configured colour first. The fallback is neutral, not `core`'s:
  // defaulting an unplaced member to `core` coloured them as belonging to a
  // lane they were never assigned — the defect ManpowerHeatmap also undid.
  const laneColor = configured ?? LANE_COLORS[nativeLane] ?? LANE_COLORS.unassigned;
  const letter = name.charAt(0).toUpperCase();

  return (
    <g opacity={opacity}>
      {ringStyle === "baseline" && (
        <circle cx={cx} cy={cy} r={8} fill="none" stroke={laneColor} strokeWidth={1} strokeOpacity={0.35} />
      )}
      {ringStyle === "under" && (
        <circle cx={cx} cy={cy} r={7} fill="none" stroke={laneColor} strokeWidth={0.7} strokeOpacity={0.22} />
      )}
      {ringStyle === "over" && (
        <circle cx={cx} cy={cy} r={10} fill="none" stroke={ALERT_COLOR} strokeWidth={2} />
      )}
      {ringStyle === "drift" && (
        <circle cx={cx} cy={cy} r={10} fill="none" stroke={ALERT_COLOR} strokeWidth={1.6} strokeDasharray="2.4 1.5" />
      )}
      <circle cx={cx} cy={cy} r={5.5} fill={laneColor} stroke="#10131c" strokeWidth={1} />
      <text
        x={cx} y={cy + 2}
        fontFamily="Inter, -apple-system, sans-serif"
        fontSize={8} fontWeight={700}
        fill="#0f1117" textAnchor="middle"
      >
        {letter}
      </text>
      {badgeText && (
        <>
          <rect
            x={cx - 10} y={cy + 11}
            width={20} height={9} rx={2}
            fill="rgba(239,95,95,0.15)"
          />
          <text
            x={cx} y={cy + 18}
            fontFamily="Inter, -apple-system, sans-serif"
            fontSize={8} fontWeight={600}
            fill={ALERT_COLOR} textAnchor="middle"
          >
            {badgeText}
          </text>
        </>
      )}
    </g>
  );
}
